import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Send, Copy, Check } from 'lucide-react';
import './WorkerAccount.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

const fmt = (s) => (s ? new Date(s).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

// Account van de medewerker voor het portaal /mijn: status + uitnodigen.
export default function WorkerAccount({ workerId }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    authFetch(`/api/mw-beheer/workers/${workerId}/account`).then((r) => (r.ok ? r.json() : null))
      .then((d) => setInfo((x) => (d ? { ...d, laatsteTekst: x?.laatsteTekst || null } : null))).catch(() => setInfo(null));
  }, [workerId]);
  useEffect(() => { setInfo(null); load(); setMsg(''); setErr(''); }, [load]);

  const invite = async (viaWhatsapp) => {
    setBusy(viaWhatsapp ? 'wa' : 'link'); setErr(''); setMsg(''); setCopied(false);
    try {
      const r = await authFetch(`/api/mw-beheer/workers/${workerId}/uitnodigen`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ whatsapp: viaWhatsapp }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Uitnodigen mislukt');
      if (viaWhatsapp) {
        if (d.whatsapp?.ok) setMsg('Uitnodiging verstuurd via WhatsApp.');
        else { setErr(`WhatsApp lukte niet (${d.whatsapp?.error || 'onbekend'}). Kopieer de tekst hieronder en stuur hem zelf.`); }
      }
      if (!viaWhatsapp || !d.whatsapp?.ok) {
        let ok = false;
        try { await navigator.clipboard.writeText(d.tekst); ok = true; } catch { /* geen klembord */ }
        setCopied(ok);
        if (!viaWhatsapp) setMsg(ok ? 'Uitnodiging gekopieerd. Plak hem in WhatsApp of een sms.' : 'Kopieer de tekst hieronder en stuur hem door.');
        setInfo((x) => ({ ...x, laatsteTekst: ok ? null : d.tekst }));
      }
      load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy('');
    }
  };

  const setActief = async (actief) => {
    if (!actief && !window.confirm('Account blokkeren? De medewerker wordt meteen uitgelogd en kan niet meer inloggen.')) return;
    setBusy('actief');
    await authFetch(`/api/mw-beheer/workers/${workerId}/actief`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ actief }),
    });
    setBusy(''); load();
  };

  if (!info || info.inAfwachting) return null;
  const acc = info.account;
  const canInvite = info.zelfKiezen || !!info.gebruikersnaamWordt;
  return (
    <section className="wacc">
      <h4><KeyRound size={16} /> Account medewerker</h4>
      {acc ? (
        <>
          <dl className="wacc-dl">
            <div><dt>Gebruikersnaam</dt><dd>{acc.gebruikersnaam}</dd></div>
            <div><dt>Status</dt><dd className={acc.actief ? 'wacc-ok' : 'wacc-off'}>{acc.actief ? 'Actief' : 'Geblokkeerd'}</dd></div>
            <div><dt>Aangemaakt</dt><dd>{fmt(acc.aangemaakt)}</dd></div>
            <div><dt>Laatst ingelogd</dt><dd>{fmt(acc.laatstIngelogd)}</dd></div>
          </dl>
          <button type="button" className="wacc-btn" disabled={busy === 'actief'} onClick={() => setActief(!acc.actief)}>
            {acc.actief ? 'Account blokkeren' : 'Weer toelaten'}
          </button>
        </>
      ) : (
        <>
          <p className="wacc-sub">
            Nog geen account.{info.uitgenodigd ? ` Uitgenodigd op ${fmt(info.uitgenodigd)}.` : ''}
            {info.zelfKiezen ? <> Heeft zich zelf ingeschreven en kiest zelf een gebruikersnaam.</>
              : info.gebruikersnaamWordt ? <> Gebruikersnaam wordt <strong>{info.gebruikersnaamWordt}</strong>.</>
              : <> <span className="wacc-off">Vul eerst een geldig telefoonnummer in.</span></>}
          </p>
          <div className="wacc-actions">
            <button type="button" className="wacc-btn wacc-primary" disabled={!!busy || !canInvite || !info.phoneOk} onClick={() => invite(true)}>
              <Send size={15} /> {busy === 'wa' ? 'Versturen…' : info.uitgenodigd ? 'Opnieuw uitnodigen via WhatsApp' : 'Uitnodigen via WhatsApp'}
            </button>
            <button type="button" className="wacc-btn" disabled={!!busy || !canInvite} onClick={() => invite(false)}>
              {copied ? <Check size={15} /> : <Copy size={15} />} {busy === 'link' ? 'Bezig…' : 'Link kopiëren'}
            </button>
          </div>
          {info.uitgenodigd && <p className="wacc-hint">Een nieuwe uitnodiging maakt de vorige link ongeldig.</p>}
        </>
      )}
      {msg && <p className="wacc-ok">{msg}</p>}
      {err && <p className="wacc-off">{err}</p>}
      {info.laatsteTekst && <textarea className="wacc-text" readOnly value={info.laatsteTekst} rows={6} onFocus={(e) => e.target.select()} />}
    </section>
  );
}
